import { useEffect, useRef } from 'react';
import { makeMutable, withDelay, withTiming, type SharedValue } from 'react-native-reanimated';

import { DURATION, STAGGER_DELAY } from '@/constants/animation';

type StaggerType = 'fade' | 'fadeUp';

interface UseStaggeredEntranceOptions {
  /** Number of items to stagger. */
  count: number;
  /** The type of entrance animation. Default: `'fadeUp'`. */
  type?: StaggerType;
  /** Delay before the first item animates (ms). Default: `0`. */
  baseDelay?: number;
  /** Delay between each item (ms). Default: `STAGGER_DELAY` (150ms). */
  staggerDelay?: number;
  /** Animation duration per item (ms). Default: `DURATION.slow` (400ms). */
  duration?: number;
  /** Vertical offset for `fadeUp` (px). Default: `12`. */
  distance?: number;
}

interface StaggeredItemStyle {
  opacity: SharedValue<number>;
  transform: { translateY: SharedValue<number> }[];
}

/** Returns animated style objects for a staggered entrance sequence. */
export function useStaggeredEntrance({
  count,
  type = 'fadeUp',
  baseDelay = 0,
  staggerDelay = STAGGER_DELAY,
  duration = DURATION.slow,
  distance = 12,
}: UseStaggeredEntranceOptions): StaggeredItemStyle[] {
  const itemsRef = useRef<StaggeredItemStyle[] | null>(null);

  if (!itemsRef.current || itemsRef.current.length !== count) {
    itemsRef.current = Array.from({ length: count }, () => ({
      opacity: makeMutable(0),
      transform: [{ translateY: makeMutable(type === 'fadeUp' ? distance : 0) }],
    }));
  }

  const items = itemsRef.current;

  useEffect(() => {
    const timing = { duration };

    items.forEach((item, index) => {
      const delay = baseDelay + index * staggerDelay;

      item.opacity.value = withDelay(delay, withTiming(1, timing));

      if (type === 'fadeUp') {
        item.transform[0].translateY.value = withDelay(delay, withTiming(0, timing));
      }
    });
  }, []);

  return items;
}
